import { PropsWithChildren, ReactNode } from "react";

import { Stack, Text } from "@bps/fluent-ui";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { hideUnlessPrintingStyles } from "@ui-components/printing/Print.styles.ts";
import { Print } from "@ui-components/printing/Print.tsx";

interface AppointmentInformationModalPrintWrapperProps
  extends PropsWithChildren {
  title: ReactNode;
  hideStatusColumn?: boolean;
}

const AppointmentInformationModalPrintWrapperBase: React.FC<
  AppointmentInformationModalPrintWrapperProps
> = ({ title, children, hideStatusColumn }) => {
  const { core } = useStores();

  const defaultOrgUnit = Array.from(core.orgUnitsMap.values()).find(
    orgUnit => orgUnit.isDefaultLocation
  );

  return (
    <Print pageStyle="@page { margin: 60px 40px; }">
      {print => (
        <div ref={print.ref}>
          <Stack
            tokens={{ childrenGap: 8 }}
            styles={{
              root: {
                height: 631,
                display: "flex",
                flexDirection: "column",
                "@media print": {
                  "[data-bp-test-element='list-wrapper']": {
                    overflowY: "visible"
                  },
                  ".ms-SelectionZone > .ms-FocusZone": {
                    display: "block"
                  },
                  ".ms-DetailsHeader-cellIsCheck, .ms-DetailsRow-cellCheck": {
                    display: "none"
                  },
                  ".ms-DetailsHeader-cell[data-item-key='duration'], .ms-DetailsRow-cell[data-automation-key='duration']":
                    {
                      display: "none"
                    },
                  ".ms-DetailsHeader-cell[data-item-key='cancellationReason'], .ms-DetailsRow-cell[data-automation-key='cancellationReason']":
                    {
                      display: "none"
                    },
                  ...(hideStatusColumn && {
                    ".ms-DetailsHeader-cell[data-item-key='status'], .ms-DetailsRow-cell[data-automation-key='status']":
                      {
                        display: "none"
                      }
                  })
                }
              }
            }}
          >
            <Stack styles={hideUnlessPrintingStyles}>{title}</Stack>
            <Stack
              styles={hideUnlessPrintingStyles}
              tokens={{ childrenGap: 4 }}
            >
              <Text
                variant="medium"
                block
                styles={{ root: { fontWeight: 600 } }}
              >
                Practice
              </Text>
              <Text variant="medium" block>
                {defaultOrgUnit?.name}
              </Text>
              <Text variant="medium" block>
                {defaultOrgUnit?.address}
              </Text>
              {defaultOrgUnit?.workPhone && (
                <Text variant="medium" block>
                  Phone: {defaultOrgUnit.workPhone}
                </Text>
              )}
            </Stack>
            {children}
          </Stack>
        </div>
      )}
    </Print>
  );
};

export const AppointmentInformationModalPrintWrapper = withFetch(
  x =>
    Array.from(x.core.orgUnitsMap.values()).map(orgUnit =>
      orgUnit.preloadIsDefaultLocation(orgUnit.id)
    ),
  AppointmentInformationModalPrintWrapperBase
);
